import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid mx-0 p-2 pt-6" }
const _hoisted_2 = { class: "p-error inline-flex align-items-center mt-0" }
const _hoisted_3 = ["textContent"]
const _hoisted_4 = {
  key: 0,
  class: "flex flex-column flex-wrap align-content-center justify-content-end ml-auto"
}
const _hoisted_5 = { class: "pb-2 flex justify-content-end" }
const _hoisted_6 = { class: "pl-1" }
const _hoisted_7 = { class: "flex justify-content-end" }
const _hoisted_8 = { class: "pl-1" }
const _hoisted_9 = ["disabled", "onClick"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { key: 1 }

import { toRefs, ref, Ref, inject, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import OrganizationContractRemoveDisciplineModal from './OrganizationContractRemoveDisciplineModal.vue';

import { useOrganizationContractsStore } from '@/store';
import { CONTRACT_ENUMS } from '@/enum';
import { DisciplineInterface } from '@/types';

interface Props {
  scope: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationContractDisciplines',
  props: {
    scope: {}
  },
  setup(__props: any) {

const props = __props;
const contractStore = useOrganizationContractsStore();
const toast = useToast();
const canEdit = contractStore.canEditOrCreateContracts;
const { availableDisciplinesData, editType } = storeToRefs(contractStore);
const { scope } = toRefs(props);
const loading = ref(true);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const removeDisciplineModalOpen = ref(false);
const disciplineToRemove = ref<DisciplineInterface>();
const disciplineContractTypes = [
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.FLAT_FEE,
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.HOURLY,
  CONTRACT_ENUMS.CONTRACT_BILLING_TYPE.PER_STUDENT,
];

onMounted(async () => {
  if (editType.value === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    await checkDisciplineEventCounts();
  }

  loading.value = false;
});

const checkDisciplineEventCounts = async () => {
  contractDisciplines.value
    .filter((discipline) => discipline.active)
    .forEach(async (discipline) => {
      const response = await contractStore.getDisciplineEventCount(discipline.id);
      discipline.event_count = response.events_count;
    });
};

const disciplinesActive = () => {
  return (
    (contractDisciplines.value as DisciplineInterface[]).filter((discipline) => {
      return discipline.active;
    }).length > 0
  );
};

const rules = computed(() => ({
  disciplines: {
    disciplinesActive: helpers.withMessage('At least one discipline must be active', disciplinesActive),
  },
}));

const contractDisciplines = computed<DisciplineInterface[]>(() =>
  sortDiscipline(availableDisciplinesData.value[disciplineContractTypes[selectedMainForm.value]]),
);

const sortDiscipline = (arr: DisciplineInterface[]) => {
  const sortedList = arr.sort((a, b) => {
    const stringA = a.discipline?.name;
    const stringB = b.discipline?.name;
    if (stringA && stringB) return stringA.localeCompare(stringB);
    else return 1;
  });

  return sortedList;
};

const removedFromContractDate = (date: string) => {
  if (date) {
    const documentByDate = new Date(date);
    const followingMonth = ((documentByDate.getMonth() + 1) % 12) + 1;
    const yearValue = followingMonth !== 1 ? documentByDate.getFullYear() : documentByDate.getFullYear() + 1;

    return `${followingMonth}/01/${yearValue}`;
  }
  return '';
};

const documentByDate = (date: string) => {
  if (date) {
    const documentByDate = new Date(date);
    const month = (documentByDate.getMonth() + 1) % 12;
    const day = documentByDate.getDate();
    const year = documentByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
};

const disciplineToggled = (discipline: DisciplineInterface) => {
  const disablingDiscipline =
    discipline.active && discipline.contract_id && editType.value === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT;
  if (disablingDiscipline) {
    disciplineToRemove.value = discipline;
    if (!contractStore.selectedContractStarted || discipline.event_count === 0) {
      onRemove({ schedule_by_date: null, document_by_date: null }, false);
      return;
    }

    discipline.active = true;
    toggleConfirmRemoveDisciplineModal();
  }
};

const onRemove = async (endDates: Record<string, string | null>, closeModal = true) => {
  if (closeModal) toggleConfirmRemoveDisciplineModal();
  await contractStore.deleteDiscipline(disciplineToRemove.value?.id as number, endDates);
  (disciplineToRemove.value as DisciplineInterface).document_by_date = endDates.document_by_date;
  (disciplineToRemove.value as DisciplineInterface).schedule_by_date = endDates.schedule_by_date;
  if (closeModal) (disciplineToRemove.value as DisciplineInterface).active = true;
  disciplineToRemove.value = undefined;
  doToast('Removed', 'Discipline will be removed from the contract after changes have been saved');
};

const onRemoveDisciplineCancel = () => {
  const disciplineToRemoveIndex = contractDisciplines.value.findIndex(
    (discipline) => discipline.discipline_id === disciplineToRemove.value?.discipline_id,
  );
  contractDisciplines.value[disciplineToRemoveIndex].active = true;
  disciplineToRemove.value = undefined;
  toggleConfirmRemoveDisciplineModal();
};

const toggleConfirmRemoveDisciplineModal = () => {
  removeDisciplineModalOpen.value = !removeDisciplineModalOpen.value;
};

const undoDisciplineRemoval = async (discipline: DisciplineInterface) => {
  await contractStore.restoreDiscipline(discipline.id);
  discipline.document_by_date = null;
  discipline.schedule_by_date = null;
  contractStore.doToast('Discipline will no longer be removed from this contract after save', 'Success', false);
};

const doToast = (summary: string, detail: string) => {
  toast.add({
    severity: 'success',
    summary,
    detail,
    life: 3000,
  });
};

const v$ = useVuelidate(rules, { disciplines: contractDisciplines }, { $scope: scope.value });

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputSwitch = _resolveComponent("InputSwitch")!
  const _component_Column = _resolveComponent("Column")!
  const _component_InputNumber = _resolveComponent("InputNumber")!
  const _component_DataTable = _resolveComponent("DataTable")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "font-bold pl-5" }, "Disciplines", -1)),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).disciplines.$errors, (error) => {
      return (_openBlock(), _createElementBlock("div", {
        key: error.$uid,
        class: "input-errors p-error mx-3"
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_Icon, {
            name: "info",
            class: "discipline-error-icon mr-1 text-alert-red"
          }),
          _createTextVNode(" " + _toDisplayString(error.$message), 1)
        ])
      ]))
    }), 128)),
    _createElementVNode("div", {
      class: _normalizeClass(["mt-3 w-full", { 'p-invalid': _unref(v$).disciplines.$invalid && _unref(v$).disciplines.$dirty }])
    }, [
      _createVNode(_component_DataTable, {
        value: contractDisciplines.value,
        class: _normalizeClass(["p-datatable-contracts", { 'table-loading': loading.value }]),
        "table-style": "table-layout: fixed;",
        "data-key": "discipline_id",
        "loading-icon": "logo-spinner",
        loading: loading.value,
        "responsive-layout": "scroll"
      }, {
        empty: _withCtx(() => [...(_cache[8] || (_cache[8] = [
          _createTextVNode("No disciplines found", -1)
        ]))]),
        default: _withCtx(() => [
          (contractDisciplines.value.length)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_Column, {
                  field: "id",
                  "header-style": "width: 8%",
                  "body-style": "width: 8%; vertical-align: center;"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_InputSwitch, {
                      modelValue: data.active,
                      "onUpdate:modelValue": ($event: any) => ((data.active) = $event),
                      class: _normalizeClass(["switch", { 'p-invalid': _unref(v$).disciplines.$invalid && _unref(v$).disciplines.$dirty }]),
                      disabled: data.document_by_date || !_unref(canEdit),
                      onClick: () => disciplineToggled(data),
                      onChange: _unref(v$).disciplines.touch
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "disabled", "onClick", "onChange"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "name",
                  header: "Discipline",
                  style: {"width":"20%"}
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      textContent: _toDisplayString(data.discipline.name)
                    }, null, 8, _hoisted_3)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, { "body-style": "width: 30%" }, {
                  body: _withCtx(({ data }) => [
                    (data.document_by_date)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _createElementVNode("span", _hoisted_5, [
                            _cache[9] || (_cache[9] = _createTextVNode(" Will no longer appear on this contract after ", -1)),
                            _createElementVNode("b", _hoisted_6, _toDisplayString(removedFromContractDate(data.document_by_date)), 1)
                          ]),
                          _createElementVNode("span", _hoisted_7, [
                            _cache[10] || (_cache[10] = _createTextVNode(" Cannot create or document events after ", -1)),
                            _createElementVNode("b", _hoisted_8, _toDisplayString(documentByDate(data.document_by_date)), 1)
                          ]),
                          _createElementVNode("button", {
                            class: "p-link flex ml-auto pt-2 align-items-center",
                            disabled: !_unref(canEdit),
                            onClick: () => undoDisciplineRemoval(data)
                          }, [
                            _createVNode(_component_Icon, {
                              name: "undo",
                              size: "text-xl",
                              class: "text-primary mt-1"
                            }),
                            _cache[11] || (_cache[11] = _createElementVNode("span", { class: "p-button-label text-primary ml-1" }, [
                              _createElementVNode("b", null, "Undo Remove")
                            ], -1))
                          ], 8, _hoisted_9)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                (_unref(selectedMainForm) !== _unref(CONTRACT_ENUMS).FORMS.PER_STUDENT)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (_unref(selectedMainForm) === _unref(CONTRACT_ENUMS).FORMS.HOURLY)
                        ? (_openBlock(), _createBlock(_component_Column, {
                            key: 0,
                            field: "rate",
                            header: "Billing Rate",
                            "header-style": "width: 8em",
                            "body-class": "w-1"
                          }, {
                            body: _withCtx(({ data }) => [
                              _createVNode(_component_InputNumber, {
                                modelValue: data.contract_type_data.rate,
                                "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.rate) = $event),
                                mode: "currency",
                                currency: "USD",
                                locale: "en-US",
                                "allow-empty": "",
                                class: "w-10",
                                disabled: !data.active || !_unref(canEdit),
                                onFocus: _cache[0] || (_cache[0] = (e) => e?.target?.select())
                              }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      (_unref(selectedMainForm) === _unref(CONTRACT_ENUMS).FORMS.FLAT_FEE)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(_component_Column, {
                              field: "rate",
                              header: "Annual Rate",
                              "header-class": "w-6rem",
                              "body-class": "w-1"
                            }, {
                              body: _withCtx(({ data }) => [
                                _createVNode(_component_InputNumber, {
                                  modelValue: data.contract_type_data.annual_rate,
                                  "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.annual_rate) = $event),
                                  mode: "currency",
                                  currency: "USD",
                                  locale: "en-US",
                                  "allow-empty": "",
                                  "input-class": "w-6rem text-sm",
                                  disabled: !data.active || !_unref(canEdit),
                                  onFocus: _cache[1] || (_cache[1] = (e) => e?.target?.select())
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_Column, {
                              field: "hours",
                              header: "Hours Contracted",
                              "header-class": "w-6rem",
                              "body-class": "w-1"
                            }, {
                              body: _withCtx(({ data }) => [
                                _createVNode(_component_InputNumber, {
                                  modelValue: data.contract_type_data.hours_contracted,
                                  "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.hours_contracted) = $event),
                                  mode: "decimal",
                                  "max-fraction-digits": 2,
                                  "min-fraction-digits": 0,
                                  min: 0,
                                  locale: "en-US",
                                  "input-class": "w-6rem text-sm",
                                  disabled: !data.active || !_unref(canEdit),
                                  onFocus: _cache[2] || (_cache[2] = (e) => e?.target?.select())
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_Column, {
                              field: "fte",
                              header: "Evaluations Only",
                              "header-class": "w-8rem",
                              "body-class": "text-center w-1"
                            }, {
                              body: _withCtx(({ data }) => [
                                (data.active)
                                  ? (_openBlock(), _createBlock(_component_InputSwitch, {
                                      key: 0,
                                      modelValue: data.contract_type_data.evaluations,
                                      "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.evaluations) = $event),
                                      "true-value": 1,
                                      "false-value": 0,
                                      class: "switch",
                                      disabled: !data.active || !_unref(canEdit)
                                    }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]))
                                  : (_openBlock(), _createElementBlock("span", _hoisted_11, "-"))
                              ]),
                              _: 1
                            })
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : _createCommentVNode("", true),
                (_unref(selectedMainForm) === _unref(CONTRACT_ENUMS).FORMS.PER_STUDENT)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createVNode(_component_Column, {
                        field: "per_student_rate",
                        header: "PSPY Fee"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_InputNumber, {
                            modelValue: data.contract_type_data.per_student_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.per_student_rate) = $event),
                            mode: "currency",
                            currency: "USD",
                            locale: "en-US",
                            min: 0,
                            "input-class": "w-6rem",
                            disabled: !data.active || !_unref(canEdit),
                            onFocus: _cache[3] || (_cache[3] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "per_month_rate_fee",
                        header: "PSPM Fee"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_InputNumber, {
                            modelValue: data.contract_type_data.per_month_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.per_month_rate) = $event),
                            mode: "currency",
                            currency: "USD",
                            locale: "en-US",
                            "input-class": "w-6rem",
                            disabled: !data.active || !_unref(canEdit),
                            onFocus: _cache[4] || (_cache[4] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "hours_per_week",
                        header: "Hours Per Week"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_InputNumber, {
                            modelValue: data.contract_type_data.hours_per_week,
                            "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.hours_per_week) = $event),
                            mode: "decimal",
                            "max-fraction-digits": 2,
                            "min-fraction-digits": 0,
                            min: 0,
                            locale: "en-US",
                            "input-class": "w-6rem",
                            disabled: !data.active || !_unref(canEdit),
                            onFocus: _cache[5] || (_cache[5] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "minimum",
                        header: "Seats"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_InputNumber, {
                            modelValue: data.contract_type_data.minimum,
                            "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.minimum) = $event),
                            min: 0,
                            "input-class": "w-6rem",
                            disabled: !data.active || !_unref(canEdit),
                            onFocus: _cache[6] || (_cache[6] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_Column, {
                        field: "weeks_in_year",
                        header: "Weeks in a Year"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_InputNumber, {
                            modelValue: data.contract_type_data.weeks_in_year,
                            "onUpdate:modelValue": ($event: any) => ((data.contract_type_data.weeks_in_year) = $event),
                            min: 0,
                            "input-class": "w-6rem",
                            disabled: !data.active || !_unref(canEdit),
                            onFocus: _cache[7] || (_cache[7] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                        ]),
                        _: 1
                      })
                    ], 64))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "class", "loading"])
    ], 2),
    (removeDisciplineModalOpen.value)
      ? (_openBlock(), _createBlock(OrganizationContractRemoveDisciplineModal, {
          key: 0,
          discipline: disciplineToRemove.value as DisciplineInterface,
          onRemove: onRemove,
          onRemoveCancel: onRemoveDisciplineCancel
        }, null, 8, ["discipline"]))
      : _createCommentVNode("", true)
  ]))
}
}

})